<template>
    <div class="ppv2-space-y-6">
        <!-- Adresse complète -->
        <BaseInput
            v-model="formData.address"
            :label="$t('configuration.locationForm.fields.address.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.address.description')"
            :placeholder="$t('configuration.locationForm.fields.address.placeholder')"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </template>
        </BaseInput>

        <!-- Complément d'adresse -->
        <BaseInput
            v-model="formData.complement"
            :label="$t('configuration.locationForm.fields.complement.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.complement.description')"
            :placeholder="$t('configuration.locationForm.fields.complement.placeholder')"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Ville -->
        <BaseInput
            v-model="formData.city"
            :label="$t('configuration.locationForm.fields.city.label')"
            :placeholder="$t('configuration.locationForm.fields.city.placeholder')"
            :error="errors.city"
            :state="errors.city ? 'error' : 'default'"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Code postal -->
        <BaseInput
            v-model="formData.postal_code"
            :label="$t('configuration.locationForm.fields.postalCode.label')"
            :placeholder="$t('configuration.locationForm.fields.postalCode.placeholder')"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Pays -->
        <BaseSelect
            v-model="formData.pays.id"
            :label="$t('configuration.locationForm.fields.country.label')"
            :options="countries"
            option-label="name"
            option-value="id"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </template>
        </BaseSelect>

        <!-- Interactive Map -->
        <InteractiveMap
            ref="interactiveMapRef"
            :label="$t('configuration.locationForm.interactiveMap.label')"
            :tooltipMsg="$t('configuration.locationForm.interactiveMap.description')"
            @coordinates-updated="handleCoordinates"
        />

        <!-- Is Billing Address Same Head Office -->
        <BaseRadioGroup
            v-if="card?.id === 3"
            v-model="formData.is_same_billing_address"
            :label="$t('configuration.locationForm.fields.isSameBillingAddress.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.isSameBillingAddress.description')"
            :options="isBillingAddressSameHeadOfficeOptions"
            @input="onBillingAddressChange"
            required
        />
    </div>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import InteractiveMap from '../../../Shared/InteractiveMap.vue'
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'

export default {
    name: 'LocationForm',
    props: {
        card: {
            required: true,
        },
        countries: {
            required: true,
        },
    },
    components: {
        BaseInput,
        BaseSelect,
        InfoMessageBox,
        InteractiveMap,
        BaseRadioGroup,
    },
    data() {
        return {
            formData: {
                address: '',
                complement: '',
                city: '',
                postal_code: '',
                pays: {
                    id: null,
                    name: '',
                },
                map_coords: null,
                is_same_billing_address: true,
            },
            errors: {},
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        isBillingAddressSameHeadOfficeOptions() {
            return [
                { label: this.$t('configuration.tax.fields.vatApplicable.options.yes'), value: true },
                { label: this.$t('configuration.tax.fields.vatApplicable.options.no'), value: false },
            ]
        },
        fullAddressQuery() {
            const { address, city, postal_code, pays, is_same_billing_address } = this.formData
            return [address, city, postal_code, pays?.name, is_same_billing_address].filter(Boolean).join(', ')
        },
    },
    watch: {
        'formData.pays.id'(newId) {
            const match = this.countries.find((c) => c.id === newId)
            if (match) {
                this.formData.pays.name = match.name
            } else {
                this.formData.pays.name = ''
            }
        },
        fullAddressQuery: 'geocodeAddress',
        establishment: {
            handler() {
                this.initializeForm()
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
            },
            deep: true,
        },
    },
    created() {
        if (this.formData?.map_coords && (this.formData.map_coords.lat || this.formData.map_coords.lng)) {
            const { lat, lng } = this.formData.map_coords
            setTimeout(() => {
                this.$refs.interactiveMapRef?.updateMapCenter(lat, lng)
            }, 1000)
        }

        // if (
        //     this.establishment.head_office_address != null ||
        //     this.establishment.head_office_country_id != null ||
        //     this.establishment.head_office_city != null
        // ) {
        //     this.formData.is_same_billing_address = false
        // } else {
        //     this.formData.is_same_billing_address = true
        // }
    },
    methods: {
        handleCoordinates(coords) {
            this.formData.map_coords = coords
        },
        onBillingAddressChange(newValue) {
            this.formData.is_same_billing_address = newValue
            if (newValue) {
                Object.assign(this.formData, {
                    head_office_address: null,
                    head_office_complement_address: null,
                    head_office_city: null,
                    head_office_postal_code: null,
                    head_office_country_id: null,
                    head_office_timezone: null,
                    head_office_map_coords: null,
                })
            }
            this.$forceUpdate()
            this.$emit('update', this.formData)
        },
        initializeForm() {
            const fields = ['address', 'complement', 'city', 'postal_code', 'pays', 'map_coords', 'is_same_billing_address']
            fields.forEach((field) => {
                if (this.establishment[field] !== undefined) {
                    this.formData[field] = this.establishment[field]
                }
            })
        },
        getFormData() {
            return this.formData
        },
        validateForm() {
            const required = ['address', 'city', 'postal_code', 'pays', 'is_same_billing_address']
            return required.every((field) => {
                const val = this.formData[field]
                return val && typeof val === 'string' ? val.trim() !== '' : val !== null
            })
        },
        resetForm() {
            this.formData = {
                address: '',
                complement: '',
                city: '',
                postal_code: '',
                pays: { id: null, name: '' },
                map_coords: null,
                is_same_billing_address: true,
            }
        },
        async geocodeAddress() {
            const query = this.fullAddressQuery
            if (!query) return
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
                )
                const data = await response.json()

                if (data && data.length > 0) {
                    const { lat, lon } = data[0]
                    const coords = { lat: parseFloat(lat), lng: parseFloat(lon) }

                    this.formData.map_coords = coords

                    // Envoyer les nouvelles coordonnées à la carte
                    this.$refs.interactiveMapRef?.updateMapCenter(coords.lat, coords.lng)
                }
            } catch (err) {
                console.error('Geocoding failed:', err)
            }
        },
    },
}
</script>
